import { forwardRef } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { AutoAwesome, East } from '@mui/icons-material';

import { COLORS } from 'components/AI/DeterministicFileNameManagement/constants';

export interface FileRenameCoachmarkModalProps {
  open: boolean;
  originalName: string;
  suggestedName: string;
  foundInDocumentLabels: string[];
  onMaybeLater: () => void;
  onShowWhereToAccept: () => void;
  /** Backdrop visible; set false during fly animation */
  showBackdrop?: boolean;
}

/** ~30% wider than MUI `sm` (600px) for a guided onboarding moment */
const MODAL_WIDTH_PX = 780;

const originalNameBoxSx = {
  flex: 1,
  minWidth: 0,
  px: 2,
  py: 2,
  borderRadius: 1.5,
  border: `1px solid ${COLORS.surface.divider}`,
  bgcolor: '#fff',
};

const suggestedNameBoxSx = {
  flex: 1.15,
  minWidth: 0,
  px: 2.5,
  py: 2.25,
  borderRadius: 1.5,
  border: `2px solid ${COLORS.suggestion.cardBorder}`,
  bgcolor: COLORS.suggestion.cardBg,
  boxShadow: '0 2px 12px rgba(25, 118, 210, 0.12)',
};

export const FileRenameCoachmarkModal = forwardRef<
  HTMLDivElement,
  FileRenameCoachmarkModalProps
>((
  {
    open,
    originalName,
    suggestedName,
    foundInDocumentLabels,
    onMaybeLater,
    onShowWhereToAccept,
    showBackdrop = true,
  },
  suggestedNameRef,
) => (
    <Dialog
      open={open}
      onClose={onMaybeLater}
      maxWidth={false}
      aria-labelledby="file-rename-coachmark-title"
      aria-describedby="file-rename-coachmark-description"
      BackdropProps={{
        sx: {
          opacity: showBackdrop ? 1 : 0,
          backgroundColor: 'rgba(33, 43, 54, 0.55)',
          transition: 'opacity 0.25s ease',
        },
      }}
      PaperProps={{
        sx: {
          width: `min(${MODAL_WIDTH_PX}px, calc(100vw - 32px))`,
          maxWidth: 'none',
          borderRadius: 2.5,
          overflow: 'visible',
          boxShadow: '0 24px 48px rgba(33, 43, 54, 0.18)',
        },
      }}
    >
      <DialogContent sx={{ px: 4, pt: 4.5, pb: 3 }}>
        <Stack alignItems="center" spacing={2} textAlign="center" sx={{ mb: 4 }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: COLORS.modal.headerGradient,
              border: `1px solid ${COLORS.modal.headerBorder}`,
              boxShadow: '0 4px 16px rgba(25, 118, 210, 0.1)',
            }}
          >
            <AutoAwesome sx={{ color: COLORS.icon.action, fontSize: 40 }} />
          </Box>
          <Typography
            id="file-rename-coachmark-title"
            variant="h5"
            component="h2"
            fontWeight={700}
            color={COLORS.text.primary}
            sx={{ fontSize: { xs: '1.35rem', sm: '1.5rem' }, lineHeight: 1.3 }}
          >
            Liscio found a better file name
          </Typography>
          <Typography
            id="file-rename-coachmark-description"
            variant="body1"
            color={COLORS.text.secondary}
            sx={{ maxWidth: 520, lineHeight: 1.55, fontSize: '1rem' }}
          >
            We read the document and suggested a clearer name so your files stay
            organized.
          </Typography>
        </Stack>

        <Box
          sx={{
            p: 2.5,
            borderRadius: 2,
            bgcolor: COLORS.modal.preview.bg,
            border: `1px solid ${COLORS.modal.preview.border}`,
          }}
        >
          <Stack
            direction="row"
            alignItems="stretch"
            spacing={1.5}
            sx={{ minHeight: 88 }}
          >
            <Box sx={originalNameBoxSx}>
              <Typography
                variant="overline"
                color={COLORS.text.disabled}
                display="block"
                sx={{ mb: 1, letterSpacing: 0.6, fontWeight: 600 }}
              >
                Original file name
              </Typography>
              <Typography
                variant="body1"
                color={COLORS.text.secondary}
                sx={{ wordBreak: 'break-word', lineHeight: 1.45 }}
              >
                {originalName}
              </Typography>
            </Box>

            <Stack alignItems="center" justifyContent="center" sx={{ px: 0.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#fff',
                  border: `1px solid ${COLORS.modal.preview.border}`,
                }}
              >
                <East sx={{ color: COLORS.icon.action, fontSize: 22 }} />
              </Box>
            </Stack>

            <Box ref={suggestedNameRef} sx={suggestedNameBoxSx}>
              <Typography
                variant="overline"
                color={COLORS.icon.action}
                display="block"
                sx={{ mb: 1, letterSpacing: 0.6, fontWeight: 700 }}
              >
                Suggested file name
              </Typography>
              <Typography
                variant="h6"
                component="p"
                fontWeight={700}
                color={COLORS.text.primary}
                sx={{
                  wordBreak: 'break-word',
                  lineHeight: 1.4,
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                }}
              >
                {suggestedName}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {foundInDocumentLabels.length > 0 && (
          <Box
            sx={{
              mt: 3.5,
              pt: 2.5,
              borderTop: `1px solid ${COLORS.surface.divider}`,
            }}
          >
            <Typography
              variant="subtitle2"
              color={COLORS.text.primary}
              fontWeight={600}
              sx={{ mb: 1.75 }}
            >
              Liscio found in this document
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1.25}>
              {foundInDocumentLabels.map((label) => (
                <Box
                  key={label}
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 1.75,
                    py: 0.875,
                    borderRadius: 999,
                    fontSize: '0.9375rem',
                    fontWeight: 500,
                    lineHeight: 1.25,
                    color: COLORS.chip.view.text,
                    bgcolor: '#fff',
                    border: `1px solid ${COLORS.chip.view.border}`,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  }}
                >
                  {label}
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          pb: 4,
          pt: 1,
          gap: 2,
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Button
          variant="text"
          color="inherit"
          onClick={onMaybeLater}
          sx={{
            color: COLORS.text.secondary,
            fontSize: '0.9375rem',
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Maybe later
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={onShowWhereToAccept}
          sx={{
            bgcolor: COLORS.modal.save,
            '&:hover': { bgcolor: COLORS.modal.saveHover },
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.9375rem',
            px: 3,
            py: 1.25,
            boxShadow: '0 4px 12px rgba(51, 102, 204, 0.35)',
          }}
        >
          Show me where to accept
        </Button>
      </DialogActions>
    </Dialog>
),
);
